import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate, formatTimestamp } from "../lib/format";
import {
  api,
  getApiKey,
  setApiKey,
  type ModelRow,
  type ProviderView,
} from "../lib/api";
import {
  curlSnippet,
  getDefaultModel,
  jsSnippet,
  loadRuntimeConfig,
  openaiSdkSnippet,
  pySnippet,
  setDefaultModel,
  isPolicyModel,
  POLICY_META,
  type RuntimeConfig,
} from "../lib/developer";
import { ModelSelector } from "../components/ModelSelector";
import {
  CopyButton,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  ProviderMark,
  SectionHeader,
  SkeletonRows,
  StatusBadge,
  toneForStatus,
} from "../components/ui";

interface KeyRow {
  id: string;
  name: string;
  prefix: string;
  projectId: string;
  environment: string;
  rateLimitPerMinute: number | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

type Lang = "sdk" | "curl" | "js" | "py";

const ENDPOINTS: Array<{ name: string; method: string; path: string; note: string }> = [
  { name: "Chat completions", method: "POST", path: "/v1/chat/completions", note: "SSE via stream:true" },
  { name: "Models", method: "GET", path: "/v1/models", note: "OpenAI-compatible list" },
  { name: "Embeddings", method: "POST", path: "/v1/embeddings", note: "Routed like chat" },
  { name: "Route simulate", method: "POST", path: "/v1/route/simulate", note: "NEXUS extension — decision without execution" },
];

export default function KeysPage() {
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState("development");
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Section A — NEXUS access
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [model, setModel] = useState(getDefaultModel());
  const [lang, setLang] = useState<Lang>("sdk");
  const [revealed, setRevealed] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [consoleKey, setConsoleKey] = useState(getApiKey());

  async function load() {
    try {
      const res = await api<{ data: KeyRow[] }>("/api/keys");
      setKeys(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load keys");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    loadRuntimeConfig().then(setConfig).catch(() => {});
    api<{ data: ModelRow[] }>("/api/models").then((r) => setModels(r.data)).catch(() => {});
    api<{ data: ProviderView[] }>("/api/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const baseUrl = config?.publicBaseUrl ?? "";
  const apiKey = consoleKey;
  const maskedKey = useMemo(
    () => (apiKey ? `${apiKey.slice(0, 10)}••••••••` : ""),
    [apiKey],
  );
  // The console key row, when it corresponds to a managed API key — enables Regenerate.
  const consoleKeyRow = useMemo(
    () => keys.find((k) => !k.revokedAt && apiKey.startsWith(k.prefix)),
    [keys, apiKey],
  );
  const policy = isPolicyModel(model) ? POLICY_META[model] : null;

  function onModelChange(id: string) {
    setModel(id);
    setDefaultModel(id);
  }

  const snippet = { baseUrl, model, prompt: "Hello, NEXUS", stream: false };
  const SNIPPETS: Record<Lang, { label: string; code: string }> = {
    sdk: { label: "OpenAI SDK", code: openaiSdkSnippet(snippet) },
    curl: { label: "cURL", code: curlSnippet(snippet) },
    js: { label: "JavaScript", code: jsSnippet(snippet) },
    py: { label: "Python", code: pySnippet(snippet) },
  };

  // Provider credentials summary — real connected state, non-demo only.
  const prodProviders = useMemo(
    () => providers.filter((p) => !p.providerTypeId.startsWith("mock-")),
    [providers],
  );

  async function regenerateConsoleKey() {
    if (!consoleKeyRow) return;
    setRegenBusy(true);
    try {
      const res = await api<{ secret: string }>(`/api/keys/${consoleKeyRow.id}/rotate`, {
        method: "POST",
      });
      if (res.secret) {
        setApiKey(res.secret);
        setConsoleKey(res.secret);
        setCreatedSecret(res.secret);
        setRevealed(false);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Regenerate failed");
    } finally {
      setRegenBusy(false);
    }
  }

  async function createKey() {
    setBusy(true);
    setCreatedSecret(null);
    try {
      const res = await api<{ secret: string; prefix: string }>("/api/keys", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), environment }),
      });
      setCreatedSecret(res.secret);
      setName("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  async function act(id: string, action: "revoke" | "rotate") {
    try {
      const res = await api<{ secret?: string }>(`/api/keys/${id}/${action}`, {
        method: "POST",
      });
      if (res.secret) setCreatedSecret(res.secret);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action} failed`);
    }
  }

  return (
    <div className="mx-auto max-w-3xl pb-16">
      <PageHeader
        title="Keys"
        subtitle="Your NEXUS credentials and provider connections."
      />

      {createdSecret && (
        <div className="panel mb-5 border-accent/40 bg-accent/5 p-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-semibold text-accent">
              Copy this key — it won't be shown again
            </span>
            <CopyButton text={createdSecret} label="Copy key" />
          </div>
          <code className="mono mt-2 block break-all rounded-lg bg-canvas px-3 py-2.5 text-sm">
            {createdSecret}
          </code>
        </div>
      )}

      {/* ── Section A: NEXUS API access ─────────────────────────── */}
      <section className="mb-8">
        <SectionHeader
          title="NEXUS API access"
          subtitle="Your application's credentials for calling NEXUS — one OpenAI-compatible API for your entire inference pool."
        />

        <div className="mb-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-canvas-muted">
            NEXUS API key
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="mono min-w-0 flex-1 truncate rounded-lg border border-canvas-border bg-canvas-raised px-3.5 py-2.5 text-sm">
              {revealed ? apiKey : maskedKey || "Not configured"}
            </code>
            <button
              className="btn-ghost !px-3 !py-2 text-xs"
              onClick={() => setRevealed((v) => !v)}
            >
              {revealed ? "Hide" : "Show"}
            </button>
            <CopyButton text={apiKey} label="Copy" />
            {consoleKeyRow && (
              <button
                className="btn-ghost !px-3 !py-2 text-xs text-warn hover:border-warn/50"
                disabled={regenBusy}
                onClick={() => {
                  if (
                    confirm(
                      `Regenerate "${consoleKeyRow.name}"? The current secret stops working immediately and this console will switch to the new one.`,
                    )
                  )
                    void regenerateConsoleKey();
                }}
              >
                {regenBusy ? "…" : "Regenerate"}
              </button>
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-canvas-muted">
            This is your console key — it calls <code className="mono">/v1</code> directly.
            For apps, create a scoped key below.
          </p>
        </div>

        <div className="mb-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-canvas-muted">
            NEXUS Base URL
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="mono min-w-0 flex-1 truncate rounded-lg border border-canvas-border bg-canvas-raised px-3.5 py-2.5 text-sm">
              {baseUrl ? `${baseUrl}/v1` : "Not configured"}
            </code>
            <CopyButton text={`${baseUrl}/v1`} label="Copy" />
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-canvas-muted">
            Default model
          </div>
          <ModelSelector
            models={models}
            providers={providers}
            value={model}
            onChange={onModelChange}
          />
          <p className="mt-1.5 text-[11px] text-canvas-muted">
            {policy
              ? `${policy.name} — ${policy.tagline}. NEXUS resolves it to a real deployment per request.`
              : "Specific deployment — bypasses routing policies."}
          </p>
        </div>

        <div className="mb-6">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-canvas-muted">
            Endpoints
          </div>
          <div className="divide-y divide-canvas-border rounded-lg border border-canvas-border">
            {ENDPOINTS.map((e) => (
              <div key={e.path} className="flex items-center justify-between gap-3 px-3.5 py-2">
                <div className="min-w-0">
                  <div className="text-sm">{e.name}</div>
                  <div className="text-[11px] text-canvas-muted">{e.note}</div>
                </div>
                <code className="mono shrink-0 text-xs text-canvas-muted">
                  <span className={e.method === "POST" ? "text-accent" : "text-info"}>{e.method}</span>{" "}
                  {e.path}
                </code>
              </div>
            ))}
          </div>
        </div>

        {/* Quick start */}
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-canvas-muted">
            Quick start
          </div>
          <div className="flex gap-1.5 border-b border-canvas-border">
            {(Object.keys(SNIPPETS) as Lang[]).map((l) => (
              <button
                key={l}
                className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition ${
                  lang === l
                    ? "border-accent text-canvas-ink"
                    : "border-transparent text-canvas-muted hover:text-canvas-ink"
                }`}
                onClick={() => setLang(l)}
              >
                {SNIPPETS[l].label}
              </button>
            ))}
          </div>
          <div className="relative mt-3">
            <pre className="overflow-x-auto rounded-lg border border-canvas-border bg-canvas p-4 text-xs leading-relaxed">
              {SNIPPETS[lang].code}
            </pre>
            <div className="absolute right-3 top-3">
              <CopyButton text={SNIPPETS[lang].code} label="Copy" />
            </div>
          </div>
          <p className="mt-2 text-xs text-canvas-muted">
            Set <code className="mono">NEXUS_API_KEY</code> in your environment — never commit it.
          </p>
        </div>
      </section>

      {/* ── Section B: Provider credentials ─────────────────────── */}
      <section className="mb-8">
        <SectionHeader
          title="Provider credentials"
          subtitle="Credentials NEXUS uses to reach external AI providers — not the key your app uses to call NEXUS."
          right={
            <Link to="/providers" className="text-xs text-accent hover:underline">
              Manage providers →
            </Link>
          }
        />
        {prodProviders.length === 0 ? (
          <EmptyState
            title="No providers connected"
            body="Connect a provider to give NEXUS inference capacity — free sources work with no key."
            action={
              <Link to="/providers" className="btn-ghost text-xs">Add provider →</Link>
            }
          />
        ) : (
          <div className="divide-y divide-canvas-border rounded-lg border border-canvas-border">
            {prodProviders.map((p) => {
              const cred = p.credentials;
              const credLabel =
                cred?.state === "configured"
                  ? "Configured"
                  : cred?.state === "anonymous" || cred?.state === "none_required"
                    ? "No key needed"
                    : "Not configured";
              return (
                <div key={p.id} className="flex items-center gap-3 px-3.5 py-2.5">
                  <ProviderMark
                    letter={p.definition.branding.letter}
                    accent={p.definition.branding.accent}
                    size={24}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{p.name}</div>
                    <div className="text-[11px] text-canvas-muted">
                      {p.modelCount} model{p.modelCount === 1 ? "" : "s"}
                      {p.freeModelCount > 0 && ` · ${p.freeModelCount} free`}
                    </div>
                  </div>
                  <span className="text-xs text-canvas-muted">{credLabel}</span>
                  <StatusBadge
                    label={p.connectionStatus.replace(/_/g, " ")}
                    tone={toneForStatus(p.connectionStatus)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Section C: API key management ───────────────────────── */}
      <section>
        <SectionHeader
          title="API keys"
          subtitle="Scoped keys for your applications — shown once, then masked."
        />
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. production-app"
              className="w-64 rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
            />
          </Field>
          <Field label="Environment">
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
            >
              <option value="development">development</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </select>
          </Field>
          <button
            className="btn-primary"
            onClick={() => void createKey()}
            disabled={busy || !name.trim()}
          >
            {busy ? "Creating…" : "Create key"}
          </button>
        </div>

        {error && <div className="mb-4"><ErrorState message={error} onRetry={load} /></div>}

        {loading ? (
          <SkeletonRows rows={3} height={52} />
        ) : keys.length === 0 ? (
          <EmptyState
            title="No API keys"
            body="Create a key to let applications call the NEXUS gateway."
          />
        ) : (
          <div className="panel overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-canvas-border">
                  <th className="table-th">Name</th>
                  <th className="table-th">Prefix</th>
                  <th className="table-th">Env</th>
                  <th className="table-th">Created</th>
                  <th className="table-th">Last used</th>
                  <th className="table-th">Status</th>
                  <th className="table-th"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-canvas-border">
                {keys.map((k) => (
                  <tr key={k.id} className={k.revokedAt ? "opacity-50" : ""}>
                    <td className="table-td font-medium">
                      {k.name}
                      {consoleKeyRow?.id === k.id && (
                        <span className="chip-accent ml-1.5 !text-[10px]">console</span>
                      )}
                    </td>
                    <td className="table-td mono">{k.prefix}…</td>
                    <td className="table-td">
                      <StatusBadge
                        label={k.environment}
                        tone={k.environment === "production" ? "warn" : "muted"}
                      />
                    </td>
                    <td className="table-td mono text-canvas-muted">
                      {formatDate(k.createdAt)}
                    </td>
                    <td className="table-td mono text-canvas-muted">
                      {k.lastUsedAt ? formatTimestamp(k.lastUsedAt) : "never"}
                    </td>
                    <td className="table-td">
                      <StatusBadge
                        label={k.revokedAt ? "revoked" : "active"}
                        tone={k.revokedAt ? "danger" : "ok"}
                      />
                    </td>
                    <td className="table-td text-right">
                      {!k.revokedAt && (
                        <div className="flex justify-end gap-1">
                          <button
                            className="btn-ghost !px-2.5 !py-1 text-xs"
                            onClick={() => {
                              if (confirm("Rotate this key? The old secret stops working immediately."))
                                void act(k.id, "rotate");
                            }}
                          >
                            Rotate
                          </button>
                          <button
                            className="btn-ghost !px-2.5 !py-1 text-xs text-danger hover:border-danger/50"
                            onClick={() => {
                              if (confirm(`Revoke "${k.name}"? Applications using it will lose access.`))
                                void act(k.id, "revoke");
                            }}
                          >
                            Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
